import { formatStaffName } from "@/lib/format-data";

import type { WaitlistAssignment, WaitlistProviderRef } from "@/types/waitlist";

/** The providers an occurrence names; empty means any eligible provider. */
export function acceptableProviderIds(
  assignment: Pick<WaitlistAssignment, "providerId" | "providerIds">,
): string[] {
  return assignment.providerId
    ? [assignment.providerId]
    : (assignment.providerIds ?? []);
}

/** One choice is a provider; two or more are a set, any of whom will do. */
export function withProviderChoices<T extends WaitlistAssignment>(
  assignment: T,
  providerIds: readonly string[],
): T {
  const { providerIds: _previous, ...rest } = assignment;
  const ids = Array.from(new Set(providerIds)).sort();
  return ids.length > 1
    ? ({ ...rest, providerId: null, providerIds: ids } as T)
    : ({ ...rest, providerId: ids[0] ?? null } as T);
}

/** "Any provider", one name, or "A or B" for a set. */
export function providerChoiceLabel(
  assignment: Pick<WaitlistAssignment, "providerId" | "providerIds">,
  providers: WaitlistProviderRef[],
): string {
  const ids = acceptableProviderIds(assignment);
  if (!ids.length) return "Any provider";
  const names = ids.map((id) => {
    const provider = providers.find((item) => item.id === id);
    return provider ? formatStaffName(provider) : "Selected provider";
  });
  return names.length > 1
    ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`
    : names[0];
}
